import { useState, useEffect, useRef } from "react";
import { Trophy } from "lucide-react";
import { api } from "../lib/api";

const FIRST = [
  "Ben", "Jack", "Sam", "Leo", "Kai", "Tom", "Alfie", "Harry", "Noah", "Mo",
  "Reece", "Josh", "Dylan", "Finn", "Charlie", "Ollie", "Jude", "Theo", "Riley",
  "Luca", "Mason", "Kian", "Zach", "Jay", "Freddie", "Arlo", "Rory", "Danno", "John", "Callum",
];
const ADJ = [
  "super", "mega", "elite", "the", "mr", "big", "young", "mad", "lethal", "clinical",
  "offside", "prime", "lazy", "sunday", "wonky", "peak", "classy",
];
const CORE = [
  "fpl", "gaffer", "manager", "wildcard", "captain", "bench", "scout", "treblewinner",
  "hattrick", "nutmeg", "tekkers", "assist", "keeper", "striker", "boss", "draftking",
  "clashlord", "coyg", "ynwa", "utd", "coys", "baller", "pundit",
];
const SUFFIX = ["", "_", "x", "hd", "tv", "zz", "99", "_fc"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const maybeNum = (p = 0.5) => (Math.random() < p ? String(Math.floor(Math.random() * (Math.random() < 0.5 ? 99 : 999))) : "");
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const makeName = () => {
  const sep = Math.random() < 0.35 ? "_" : "";
  switch (Math.floor(Math.random() * 6)) {
    case 0: return `${pick(ADJ)}${sep}${pick(CORE)}${maybeNum(0.4)}`;
    case 1: return `${cap(pick(FIRST))}${sep}${pick(CORE)}${maybeNum(0.5)}`;
    case 2: return `${pick(CORE)}${pick(SUFFIX)}${maybeNum(0.3)}`;
    case 3: return `${pick(ADJ)}${cap(pick(FIRST))}${maybeNum(0.6)}`;
    case 4: return `${cap(pick(FIRST))}${maybeNum(1)}`;
    default: return `${pick(CORE)}${sep}${pick(FIRST).toLowerCase()}${pick(SUFFIX)}`;
  }
};

// Virtual £ game wins (pots are 2x the stake; Pep and Daily Clash pay fixed amounts).
const WINS = [
  () => ({ amount: pick([20, 50, 100, 200]), how: "in a Draft XI wager" }),
  () => ({ amount: pick([20, 40, 100]), how: "on penalties" }),
  () => ({ amount: 10, how: "beating Pep" }),
  () => ({ amount: 15, how: "in the Daily Clash" }),
];

const recent = [];
const makeWinner = (prev) => {
  let name;
  do { name = makeName(); } while (recent.includes(name.toLowerCase()));
  recent.push(name.toLowerCase());
  if (recent.length > 60) recent.shift();
  let w;
  do { w = pick(WINS)(); } while (prev && w.amount === prev.amount);
  return { name, ...w, id: Math.random().toString(36).slice(2) };
};

// Rotating "X won £Y" strip on Home: real recent wins first, made-up ones fill the gaps.
const WinnersTicker = () => {
  const [item, setItem] = useState(() => makeWinner());
  const [show, setShow] = useState(true);
  const timers = useRef([]);
  const real = useRef([]);
  const seen = useRef(new Set());

  useEffect(() => {
    const load = () => api.recentWinners().then((list) => {
      list.forEach((w) => {
        const key = `${w.name}|${w.amount}|${w.how}`;
        if (!seen.current.has(key)) { seen.current.add(key); real.current.push({ ...w, real: true }); }
      });
    }).catch(() => {});
    load();
    const poll = setInterval(load, 60000);
    const loop = setInterval(() => {
      setShow(false);
      timers.current.push(setTimeout(() => {
        const next = real.current.shift();
        setItem((prev) => (next ? { ...next, id: Math.random().toString(36).slice(2) } : makeWinner(prev)));
        setShow(true);
      }, 300));
    }, 5000);
    return () => {
      clearInterval(loop);
      clearInterval(poll);
      timers.current.forEach(clearTimeout);
    };
  }, []);

  return (
    <div data-testid="winners-ticker" className="w-full">
      <div className="flex items-center gap-2.5 rounded-lg border border-[#334155] bg-[#1E293B] px-3 py-2">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
        </span>
        <Trophy className="w-4 h-4 text-[#F59E0B] shrink-0" />
        <span
          key={item.id}
          data-testid="winners-ticker-text"
          className={`text-sm text-slate-300 truncate transition-[opacity,transform] duration-300 ${show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"}`}
        >
          <span className="font-semibold text-white">{item.name}</span> won{" "}
          <span className="font-semibold text-[#10B981]">£{item.amount}</span> {item.how}
        </span>
      </div>
    </div>
  );
};

export default WinnersTicker;
